/**
 * HTTP 客户端 —— 唯一与后端说话的地方。
 *
 * 所有请求同源（dev 由 vite 代理 `/api` → 8080），所以 cookie 自动带上，
 * 不需要 CORS，也不需要把 session 放进 JS 可读的地方。
 *
 * 错误统一成 `ApiError`：后端把领域错误放在响应体的 `error` 字段里
 * （`not_found` / `not_paired` / `entry_conflict` / …），界面据此决定文案。
 * 不做全局的 toast —— 哪个错误该怎么呈现由调用点决定，一个统一的错误气泡
 * 会把「当前密码不对」和「会话过期」说成同一句话。
 */

import type { ExportBundle, Mood, Visibility } from "@wodiary/shared";
import type {
  AnniversaryView,
  EntryView,
  PairingView,
  ReplyView,
  SessionsResponse,
  SettingsResponse,
  SettingsProfile,
  SignedAttachment,
  User,
} from "./types.js";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    /** 响应体里的 `error` 字段；网络错误或非 JSON 响应时为 `null`。 */
    readonly code: string | null,
    /** 429 的 `retry_after_seconds`。 */
    readonly retryAfterSeconds?: number,
  ) {
    super(code ?? `http_${status}`);
    this.name = "ApiError";
  }
}

export class NetworkError extends Error {
  constructor() {
    super("网络请求失败");
    this.name = "NetworkError";
  }
}

interface ErrorBody {
  error?: unknown;
  retry_after_seconds?: unknown;
}

async function request<T>(
  path: string,
  init?: RequestInit & { json?: unknown },
): Promise<T> {
  const { json, ...rest } = init ?? {};
  let res: Response;
  try {
    res = await fetch(path, {
      ...rest,
      credentials: "same-origin",
      headers: {
        ...(json !== undefined ? { "content-type": "application/json" } : {}),
        ...(rest.headers ?? {}),
      },
      ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
    });
  } catch {
    throw new NetworkError();
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let body: unknown = null;
  if (text !== "") {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }

  if (!res.ok) {
    const err = (body ?? {}) as ErrorBody;
    const code = typeof err.error === "string" ? err.error : null;
    const retry =
      typeof err.retry_after_seconds === "number"
        ? err.retry_after_seconds
        : undefined;
    throw new ApiError(res.status, code, retry);
  }
  return body as T;
}

// ---------------------------------------------------------------------------
// 初始化 / 会话
// ---------------------------------------------------------------------------

export const api = {
  setupStatus: () =>
    request<{ initialized: boolean }>("/api/setup/status"),

  setup: (people: { username: string; password: string }[]) =>
    request<{ ok: true }>("/api/setup", { method: "POST", json: { people } }),

  login: (username: string, password: string) =>
    request<{ user: User }>("/api/login", {
      method: "POST",
      json: { username, password },
    }),

  logout: () => request<{ ok: true }>("/api/logout", { method: "POST" }),

  me: () => request<{ user: User }>("/api/me"),

  // -------------------------------------------------------------------------
  // 配对
  // -------------------------------------------------------------------------

  pairing: () => request<PairingView>("/api/pairing"),

  generateCode: () =>
    request<{ code: string; expiresAt: string }>("/api/pairing/code", {
      method: "POST",
    }),

  acceptCode: (code: string) =>
    request<PairingView>("/api/pairing/accept", {
      method: "POST",
      json: { code },
    }),

  confirmPairing: () =>
    request<PairingView>("/api/pairing/confirm", { method: "POST" }),

  // -------------------------------------------------------------------------
  // 日记
  // -------------------------------------------------------------------------

  listEntries: (filter: "all" | "theirs" | "mine") =>
    request<{ entries: EntryView[]; total: number }>(
      `/api/entries?filter=${filter}`,
    ),

  getEntry: (id: string) => request<EntryView>(`/api/entries/${id}`),

  createEntry: (input: {
    title: string;
    body: string;
    mood: Mood | null;
    visibility: Visibility;
    allow_reply: boolean;
  }) =>
    request<EntryView>("/api/entries", { method: "POST", json: input }),

  updateEntry: (
    id: string,
    input: {
      expected_updated_at: string;
      title?: string;
      body?: string;
      mood?: Mood | null;
      allow_reply?: boolean;
    },
  ) =>
    request<EntryView>(`/api/entries/${id}`, {
      method: "PATCH",
      json: input,
    }),

  setEntryVisibility: (id: string, visibility: Visibility) =>
    request<EntryView>(`/api/entries/${id}/visibility`, {
      method: "PATCH",
      json: { visibility },
    }),

  markRead: (id: string) =>
    request<EntryView>(`/api/entries/${id}/read`, { method: "POST" }),

  // -------------------------------------------------------------------------
  // 回应
  // -------------------------------------------------------------------------

  listReplies: (entryId: string) =>
    request<{ replies: ReplyView[] }>(`/api/entries/${entryId}/replies`),

  createReply: (entryId: string, body: string) =>
    request<ReplyView>(`/api/entries/${entryId}/replies`, {
      method: "POST",
      json: { body },
    }),

  deleteReply: (replyId: string) =>
    request<void>(`/api/replies/${replyId}`, { method: "DELETE" }),

  // -------------------------------------------------------------------------
  // 附件
  // -------------------------------------------------------------------------

  // -------------------------------------------------------------------------
  // 附件
  // -------------------------------------------------------------------------

  /** 上传一张图（multipart）。dims 是前端读到的像素尺寸；服务端只做夹紧。 */
  uploadAttachment: (
    entryId: string,
    file: File,
    dims: { width: number; height: number },
  ) => {
    const form = new FormData();
    form.append("file", file);
    form.append("width", String(dims.width));
    form.append("height", String(dims.height));
    return request<SignedAttachment>(`/api/entries/${entryId}/attachments`, {
      method: "POST",
      body: form,
    });
  },

  deleteAttachment: (id: string) =>
    request<void>(`/api/attachments/${id}`, { method: "DELETE" }),

  entryAttachments: (entryId: string) =>
    request<{ attachments: SignedAttachment[] } | { error: string }>(
      `/api/entries/${entryId}/attachments`,
    ),

  // -------------------------------------------------------------------------
  // 纪念日
  // -------------------------------------------------------------------------

  listAnniversaries: () =>
    request<{ anniversaries: AnniversaryView[] }>("/api/anniversaries"),

  createAnniversary: (input: {
    title: string;
    date: string;
    visibility: Visibility;
    remind_enabled: boolean;
  }) =>
    request<AnniversaryView>("/api/anniversaries", {
      method: "POST",
      json: input,
    }),

  updateAnniversary: (
    id: string,
    input: {
      title?: string;
      date?: string;
      visibility?: Visibility;
      remind_enabled?: boolean;
    },
  ) =>
    request<AnniversaryView>(`/api/anniversaries/${id}`, {
      method: "PATCH",
      json: input,
    }),

  anniversaryEntries: (id: string) =>
    request<{ entries: EntryView[] }>(`/api/anniversaries/${id}/entries`),

  // -------------------------------------------------------------------------
  // 导出
  // -------------------------------------------------------------------------

  exportStatus: () =>
    request<{ last_exported_at: string | null }>("/api/export/status"),

  runExport: () => request<ExportBundle>("/api/export", { method: "POST" }),

  // -------------------------------------------------------------------------
  // 设置
  // -------------------------------------------------------------------------

  settings: () => request<SettingsResponse>("/api/settings"),

  updateProfile: (input: {
    username?: string;
    default_visibility?: Visibility;
  }) =>
    request<Pick<SettingsProfile, "username" | "default_visibility">>(
      "/api/settings/profile",
      { method: "PATCH", json: input },
    ),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>("/api/settings/password", {
      method: "POST",
      json: {
        current_password: currentPassword,
        new_password: newPassword,
      },
    }),

  sessions: () => request<SessionsResponse>("/api/settings/sessions"),

  revokeOtherSessions: () =>
    request<{ revoked: number }>("/api/settings/sessions/revoke-others", {
      method: "POST",
    }),
};
